import Tools from '@/app-model/tools.js';

const tools = new Tools();

export default class User {

  /*
  |--------------------------------------------------------------------------
  | Get users from building
  |--------------------------------------------------------------------------
  */

  retrieveGetUserBuildingRoleRequest(buildingId, queryString = undefined) {
    return {
      section: 'emiot',
      method: 'GET',
      path: `api/user-buildings/${buildingId}/users?${queryString}`,
    };
  }

  /**
   * Retrieve users from a building
   *
   * @param $store
   * @param {int} buildingId
   * @param {string} queryString
   * @return {Promise}
   */
  fetchBuildingUsers({store: $store, buildingId}, queryString = 'with=pending-users'){
    return $store.dispatch('modernApiRequest', this.retrieveGetUserBuildingRoleRequest(buildingId, queryString));
  }

  /*
  |--------------------------------------------------------------------------
  | Update user building Role
  |--------------------------------------------------------------------------
  */

  retrieveUpdateUserBuildingRoleRequest(buildingId, userId = undefined, buildingRoleId = undefined, data = {}) {
    return {
      section: 'emiot',
      method: 'PUT',
      path: `api/user-buildings/${buildingId}/users/${userId}/building_roles/${buildingRoleId}`,
      data
    };
  }

  /**
   * Verify of user has permission to update user building role for the building
   *
   * @param $store
   * @param {int} buildingId
   * @return {boolean}
   */
  canUpdateUserBuildingRole ({store: $store, buildingId}) {
    return $store.getters.hasSitePermissions({siteId: buildingId, requiredPermissions: ['ADMIN']});
  }

  /**
   * Verify of update [building role] action can be apply to supplied user for the building
   *
   * @param $store
   * @param {Object} user
   * @param {boolean} canUpdateUserBuildingRole
   * @return {boolean}
   */
  canUpdateUserBuildingRoleToUser ({store: $store, user}, canUpdateUserBuildingRole) {
    if (user.id === null) return false;
    return user.uuid !== $store.getters.firebaseUser.uid && canUpdateUserBuildingRole;
  }

  /**
   * Update [building role] for the user in the building
   *
   * @param $store
   * @param{int}  buildingId
   * @param {int} userId
   * @param {int} buildingRoleId
   * @param {Object} data
   * @return {Promise}
   */
  updateUserBuildingRole({store: $store, buildingId}, userId, buildingRoleId, data = {}){
    return $store.dispatch('modernApiRequest', this.retrieveUpdateUserBuildingRoleRequest(buildingId, userId, buildingRoleId, data));
  }

  /*
  |--------------------------------------------------------------------------
  | Update user building title
  |--------------------------------------------------------------------------
  */

  /**
   * Retrieve possible request list for update user title for the building, require permission check and process
   *
   * @param {int} buildingId
   * @param {int} userId
   * @param {string} title
   * @return {Object[]}
   */
  retrieveUpdateUserBuildingTitleRequest(buildingId, userId = undefined, title = undefined) {
    return {
      section: 'emiot',
      method: 'PATCH',
      path: `api/user-buildings/${buildingId}/users/${userId}`,
      data: {
        title: title
      }
    };
  }

  /**
   * Verify of user has permission to update user building title for the building
   *
   * @param $store
   * @param {int} buildingId
   * @return {boolean}
   */
  canUpdateUserBuildingTitle ({store: $store, buildingId}) {
    return $store.getters.hasSitePermissions({siteId: buildingId, requiredPermissions: ['ADMIN']});
  }

  /**
   * Verify of update [building title] action can be apply to supplied user for the building
   *
   * @param $store
   * @param {Object} user
   * @param {boolean} canUpdateUserBuildingTitle
   * @return {boolean}
   */
  canUpdateUserBuildingTitleToUser ({store: $store, user}, canUpdateUserBuildingTitle) {
    if (user.id === null) return false;
    return canUpdateUserBuildingTitle;
  }

  /**
   * Update [building title] for the user in the building
   *
   * @param $store
   * @param {int} buildingId
   * @param {int} userId
   * @param {string} title
   * @return {Promise}
   */
  updateUserBuildingTitle({store: $store, buildingId}, userId, title){
    return $store.dispatch('modernApiRequest', this.retrieveUpdateUserBuildingTitleRequest(buildingId, userId, title));
  }

  /*
  |--------------------------------------------------------------------------
  | Remove user from building
  |--------------------------------------------------------------------------
  */

  /**
   * Retrieve possible request list for remove user from the building, require permission check and process
   *
   * @param {int} buildingId
   * @param {int} userId
   * @return {Object[]}
   */
  retrieveRemoveUserBuildingRoleRequest(buildingId, userId = undefined) {
    return {
      section: 'emiot',
      method: 'DELETE',
      path: `api/user-buildings/${buildingId}/users/${userId}`,
    };
  }

  /**
   * Verify of user has permission to remove user from the building
   *
   * @param $store
   * @param {int} buildingId
   * @return {boolean}
   */
  canRemoveUserBuildingRole ({store: $store, buildingId}) {
    return $store.getters.hasSitePermissions({siteId: buildingId, requiredPermissions: ['ADMIN']});
  }

  /**
   * Verify of remove user action can be apply to supplied user for the building
   *
   * @param $store
   * @param {Object} user
   * @param {boolean} canRemoveUserBuildingRole
   * @return {boolean}
   */
  canRemoveUserBuildingRoleToUser ({store: $store, user}, canRemoveUserBuildingRole) {
    if (user.id === null) return false;
    return user.uuid !== $store.getters.firebaseUser.uid && canRemoveUserBuildingRole;
  }

  /**
   * Remove the user from the building
   *
   * @param $store
   * @param {int} buildingId
   * @param {int} userId
   * @return {Promise}
   */
  removeUserBuildingRole({store: $store, buildingId}, userId){
    return $store.dispatch('modernApiRequest', this.retrieveRemoveUserBuildingRoleRequest(buildingId, userId));
  }

  /*
  |--------------------------------------------------------------------------
  | Add user to a building
  |--------------------------------------------------------------------------
  */

  retrieveAddUserBuildingRoleRequest(buildingId, buildingRoleId = undefined, data = {}) {
    return {
      section: 'emiot',
      method: 'POST',
      path: `api/user-buildings/${buildingId}/building-roles/${buildingRoleId}/users`,
      data
    };
  }

  /**
   * Verify of user has permission to add a user to the building
   *
   * @param $store
   * @param {int} buildingId
   * @param {int} buildingRoleId
   * @param {Object} data
   * @return {boolean}
   */
  canAddUserBuildingRole ({store: $store, buildingId}) {
    return $store.getters.hasSitePermissions({siteId: buildingId, requiredPermissions: ['ADMIN']});
  }

  /**
   * Verify of add [building role] action can be apply to supplied user for the building
   *
   * @param $store
   * @param {Object} user
   * @param {boolean} canAddUserBuildingRole
   * @return {boolean}
   */
  canAddUserBuildingRoleToUser ({store: $store, user}, canAddUserBuildingRole) {
    if (user.id === null) return false;
    return user.uuid !== $store.getters.firebaseUser.uid && canAddUserBuildingRole;
  }

  /**
   * Remove the user from a building
   *
   * @param $store
   * @param {int} buildingId
   * @param {int} userId
   * @param {Object} data
   * @return {Promise}
   */
  addUserBuildingRole({store: $store, buildingId}, buildingRoleId, data){
    return $store.dispatch('modernApiRequest', this.retrieveAddUserBuildingRoleRequest(buildingId, buildingRoleId, data));
  }

}